/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Async initialization helpers for the appointment registration modal.
 * @module logic/appointments/appointmentRegistrationInit
 */

import { logger, sanitizeError } from "../../utils/logger.js";
import { getCategoryById } from "../category/index.js";
import { fetchValidateRequirements } from "../businessEvents";
import { getReferenceData } from "../companyEdit";

/**
 * Fetches category metadata for the selected requirement.
 * @param {string|undefined} categoryId
 * @param {import("react-router").NavigateFunction} navigate
 * @returns {Promise<Object|null>}
 */
export const fetchRegistrationCategory = async (categoryId, navigate) => {
  if (!categoryId) {
    return null;
  }
  try {
    return await getCategoryById(categoryId, navigate);
  } catch (error) {
    logger.error("Error fetching category data:", {
      err: sanitizeError(error),
    });
    return null;
  }
};

/**
 * Loads countries, states, and towns once when the modal opens.
 * @param {import("react-router").NavigateFunction} navigate
 * @returns {Promise<{countries: Array, states: Array, towns: Array}>}
 */
export const fetchRegistrationLocationReferenceData = async (navigate) => {
  try {
    const [countriesData, statesData, townsData] = await Promise.all([
      getReferenceData("countries", navigate),
      getReferenceData("states", navigate),
      getReferenceData("towns", navigate),
    ]);
    return {
      countries: countriesData?.length > 0 ? countriesData : [],
      states: statesData?.length > 0 ? statesData : [],
      towns: townsData?.length > 0 ? townsData : [],
    };
  } catch (error) {
    logger.error("Error fetching location reference data:", {
      err: sanitizeError(error),
    });
    return { countries: [], states: [], towns: [] };
  }
};

/**
 * Revalidates the requirement so company data and missing-field maps match the backend.
 * @param {Object} options
 * @param {string|null} options.requirementId
 * @param {Object} options.participant - `{ idEvento, idEmpresaCompradora }`
 * @param {import("react-router").NavigateFunction} options.navigate
 * @param {string|null} options.activeRequirementId - Currently initialized requirement id
 * @returns {Promise<Object|null>} Fresh requirement snapshot or null when skipped/stale
 */
export const refreshRegistrationRequirementData = async ({
  requirementId,
  participant,
  navigate,
  activeRequirementId,
}) => {
  if (
    !requirementId ||
    !participant?.idEvento ||
    !participant?.idEmpresaCompradora
  ) {
    return null;
  }

  const requirements = await fetchValidateRequirements(
    participant.idEvento,
    participant.idEmpresaCompradora,
    navigate,
  );
  const freshRequirement = requirements?.find(
    (requirement) => requirement._id === requirementId,
  );

  if (!freshRequirement || activeRequirementId !== requirementId) {
    return null;
  }

  const freshCompany = freshRequirement.datosEmpresaUsuario || null;
  return {
    company: freshCompany
      ? { ...freshCompany, _lastUpdate: Date.now() }
      : null,
    selectedProfile: freshCompany?.perfilSeleccionado ?? 0,
    missingRequiredFields: freshRequirement.missingRequiredFields || null,
    missingOptionalFields: freshRequirement.missingOptionalFields || null,
  };
};

/**
 * Runs parallel initialization fetches when the modal opens for a requirement.
 * @param {Object} options
 * @param {Object|null} options.selectedRequirement
 * @param {Object} options.participant
 * @param {import("react-router").NavigateFunction} options.navigate
 * @param {string|null} options.activeRequirementId
 * @returns {Promise<{category: Object|null, locationReferenceData: Object, refreshedRequirement: Object|null}>}
 */
export const initializeRegistrationModalData = async ({
  selectedRequirement,
  participant,
  navigate,
  activeRequirementId,
}) => {
  const requirementId = selectedRequirement?._id ?? null;
  const [locationReferenceData, category, refreshedRequirement] =
    await Promise.all([
      fetchRegistrationLocationReferenceData(navigate),
      fetchRegistrationCategory(selectedRequirement?.idCategoria, navigate),
      refreshRegistrationRequirementData({
        requirementId,
        participant,
        navigate,
        activeRequirementId,
      }),
    ]);

  return { locationReferenceData, category, refreshedRequirement };
};
